import { CustomResponeData } from 'axios'
import getURLSearchParam from '../base/url'
import { getInviteCode } from '../axios/request-params'
import http from '../axios/index'
import Agent from '../agent'
import knbUse from '../knb/knb-use'
import { E_ProcessEnv, getProcessEnv } from '../base/env'
import { reportError, reportWarn } from '../owl'
import { lxMV } from '../lx'
import { isResponseError } from '../axios/response'

function requestData() {
  const queryToken = getURLSearchParam('token')
  let postData: {
    platform: number
    token?: string
    actEnrollInfo?: {
      activityId?: number
      inviteCode?: string | null
      utmSource?: string
    }
  } = {
    platform: Agent.dianping ? 2 : 1,
  }
  // 小程序需要从 query 取
  if (Agent.anyminiprogram && queryToken) {
    postData = { platform: 1, token: queryToken }
  }
  if (getProcessEnv(E_ProcessEnv.VUE_APP_ACTIVITY_ID)) {
    postData.actEnrollInfo = {
      activityId: Number(getProcessEnv(E_ProcessEnv.VUE_APP_ACTIVITY_ID)),
      inviteCode: getInviteCode(),
      utmSource: getURLSearchParam('utm_source') || getURLSearchParam('phx_wake_up_source'),
    }
  }
  return postData
}

const PCSSOConfig = {
  // PC的单点登录需要指定域名
  getSSOPrefix() {
    return getProcessEnv(E_ProcessEnv.VUE_APP_LOGOUT_HOST) || '//passport.meituan.com'
  },
  serviceKey: 'phoenix',
}
export function logout(continueUrl: string) {
  if (Agent.native) {
    // 美团 app 有可能出现token过期时，无法唤起登陆页的场景，请业务方登陆前务必主动检查当前token是否过期，如果token过期，需先调用logout桥登出
    knbUse('logout', {
      success: () => {
        reportWarn({ name: 'KNB登出', msg: encodeURI, global: true })
      },
    })
  } else {
    const redirectUrl = `${PCSSOConfig.getSSOPrefix()}/account/signout?service=${
      PCSSOConfig.serviceKey
    }&continue=${encodeURIComponent(continueUrl)}`
    window.location.href = redirectUrl
  }
}

// https://km.sankuai.com/page/187745120
export async function sso() {
  try {
    const res = await http.post<
      CustomResponeData<{
        status?: number
        message?: string
        [x: string]: string | number | undefined
      }>
    >('/user/api/v1/sso', requestData())
    lxMV('b_airbnb_tik4ascx_mv')
    return res
  } catch (e) {
    lxMV('b_airbnb_uvx7wols_mv')
    if (isResponseError(e) && e.status && e.message) {
      if (e.status === 401) {
        logout()
      }
      // 401 token无效、登录失败
      // 40104 token为空
      // 40003 账号异常，请联系客服处理
      // 40102 该美团账号没有绑定手机号,无法进行注册,请前往美团进行手机号绑定
      if ([401, 40104, 40001].indexOf(e.status) !== -1) {
        e.needLogin = e.status !== 0
      }
    } else {
      reportError({
        name: '未知错误',
        msg: e,
        global: true,
      })
    }
    throw e
  }
}

export type I_USER = Partial<{
  userId: number
  avatarUrl: string
  nickName: string
  verifyStatus: number
  inviteCode: string
  isHost?: boolean
}>

function processInfo(info: I_USER) {
  if (!info) return {}
  let avatarUrl = 'https://img.meituan.net/scarlett/a5bc351a852ca7838f106c395cc3176f1895.png'
  if (info.avatarUrl) {
    avatarUrl = info.avatarUrl.replace(/http:\/\//, 'https://')
  }
  return {
    ...info,
    avatarUrl,
  }
}

export function selfInfo(): Promise<I_USER> {
  return http.get<CustomResponeData<I_USER>>('/user/api/v1/user/info/self').then((res) => {
    return processInfo(res)
  })
}

export function getUserInfo(userId: string): Promise<I_USER> {
  return http.get<I_USER>(`/user/api/v1/user/info/${userId}`)
}

export function judgeIsNew() {
  return judge([3]).then((res) => {
    return res[3]
  })
}

export function judgeIsEdu() {
  return judge([2]).then((res) => {
    return res[2]
  })
}

interface I_SERVER_JUDGE {
  judgeResults: Record<string, 0 | 1 | boolean>
}
export function judge(userGroupIds: number[]) {
  // 可用 groupId 查询
  // select * from data_server.phx_user_group_info
  // 0 通过 1 未通过
  return http
    .post<CustomResponeData<I_SERVER_JUDGE>>('/ups/api/v1/judge/judgeUserGroup', {
      userGroupIds,
    })
    .then((res) => {
      for (const i of Object.keys(res.judgeResults)) {
        res.judgeResults[i] = res.judgeResults[i] === 1
      }
      return res.judgeResults
    })
}

export function checkBind(wxAccountId: number) {
  return http.get(`/user/api/v1/wx/checkBind/${wxAccountId}`)
}

export interface I_AUTHINFO extends I_USER {
  isLogin?: boolean
  needLogin?: boolean
}

export function auth(): Promise<I_AUTHINFO> {
  return selfInfo()
    .then((res) => {
      return {
        isLogin: true,
        ...res,
      }
    })
    .catch((err) => {
      return err
    })
}

export interface Role {
  isHost: boolean
  isSteward: boolean
}

export function getHostRole() {
  return http.get<Role>('/host/api/v1/host/getRole')
}

export function checkIsHost(checkIsSteward: boolean, redirectUrl?: string): Promise<I_AUTHINFO> {
  return selfInfo()
    .then((res) => {
      if (!res.isHost) {
        if (checkIsSteward) {
          return getHostRole().then((role) => {
            if (!role.isSteward) {
              window.location.replace(
                redirectUrl || 'https://cube.meituan.com/cube/block/b729a6bc5612595c83ed/206830/index.html',
              )
            }
            return {
              isLogin: true,
              ...res,
            }
          })
        } else {
          window.location.replace(
            redirectUrl || 'https://cube.meituan.com/cube/block/b729a6bc5612595c83ed/206830/index.html',
          )
        }
      }
      return {
        isLogin: true,
        ...res,
      }
    })
    .catch((err) => {
      return err
    })
}
